"use client";

import { forwardRef, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import { sharedInputCss, formElementDisabledBackgroundDef, formElementDisabledBackground, formElementDisabledBorderDef, formElementDisabledBorder } from "../utils/sharedCss";
import { InternalUseOnlyInput } from "./Input";
import { backgroundColorLevelCurrent, spacing4, borderRadius } from "@churchofjesuschrist/eden-style-constants";
import { ValidityStateContext, SmallContext, DisabledContext } from "../utils/context";
import { jsx as _jsx } from "react/jsx-runtime";
export const InternalUseOnlyInputGroup = styled.div.withConfig({
  componentId: "sc-cajwz5-0"
})(["position:relative;display:flex;align-items:center;width:100%;border-radius:", ";background-color:", ";", " ", " ", " &::after{content:\"\";", ";", " position:absolute;pointer-events:none;background:none;height:auto;top:0;left:0;right:0;bottom:0;}&:has(", ":focus){&::after{outline:highlight auto 1px;outline:-webkit-focus-ring-color auto 1px;outline-offset:2px;}& ", "{outline:none;}}& > ", ",& > ", ":focus,& > &::after,& div ::after{border:0;}& > button{margin:", ";}& > &,& > ", "{margin:0;}"], borderRadius, backgroundColorLevelCurrent, formElementDisabledBackgroundDef, formElementDisabledBorderDef, ({
  $disabled
}) => $disabled ? `background:${formElementDisabledBackground()}; && ${InternalUseOnlyInput} { background: transparent; }` : "", sharedInputCss, ({
  $disabled
}) => $disabled ? `border-color: ${formElementDisabledBorder()};` : "", InternalUseOnlyInput, InternalUseOnlyInput, InternalUseOnlyInput, InternalUseOnlyInput, ({
  $small
}) => $small ? "0" : spacing4, InternalUseOnlyInput);

/**
 * The `InputGroup` combines an `Input` with an `Icon`/`Button` component.
 * @param {object} props - The component props
 * @param {boolean} [props.small] - A boolean indicating if the InputGroup should be rendered in a smaller height
 * @param {boolean} [props.disabled] - If the InputGroup child inputs/buttons should be disabled
 * @param {React.ReactNode} props.children - Content to render inside the InputGroup Component. Children should contain some type of `input` or `select` element and an `icon` or `button` element.
 * @param {React.Ref} [forwardedRef] - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The InputGroup component
 */
const InputGroupComponent = ({
  children,
  small,
  disabled,
  ...props
}, forwardedRef) => {
  const internalRef = useRef();
  const ref = forwardedRef ?? internalRef;
  const validityStateContext = useContext(ValidityStateContext);
  useEffect(() => {
    // Disable all buttons inside the InputGroup when the `disabled` prop changes
    const buttons = ref.current.querySelectorAll("button");
    buttons.forEach(button => button.disabled = disabled);
  }, [disabled, ref]);
  return /*#__PURE__*/_jsx(InternalUseOnlyInputGroup, {
    $validityState: validityStateContext,
    $small: small,
    ref: ref,
    $disabled: disabled,
    ...props,
    children: /*#__PURE__*/_jsx(SmallContext.Provider, {
      value: small,
      children: /*#__PURE__*/_jsx(DisabledContext.Provider, {
        value: disabled,
        children: children
      })
    })
  });
};
const InputGroup = /*#__PURE__*/forwardRef(InputGroupComponent);
InputGroup.displayName = "InputGroup";
export default InputGroup;